import { createSlice } from '@reduxjs/toolkit';
import { commonInitialState } from '../../../utils/common/commonInitialState';
import { handleRequestThunk } from '../handleRequestThunk';
import { getImages, deleteImage, uploadFile } from './thunks/imageUploadThunks';

const SLICE_NAME = 'imageUpload';

export const imageUploadSlice = createSlice({
    name: SLICE_NAME,
    initialState: structuredClone(commonInitialState),
    reducers: {
        saveFormData(state, action) {
            state.data = action.payload;
        },
        clearFormData(state) {
            state.data = [];
        },
        removeImageFromData(state, action) {
            const urlToRemove = action.payload;
            if (Array.isArray(state.data)) {
                state.data = state.data.filter((url) => url !== urlToRemove);
            }
        },
        reorderImagesInState(state, action) {
            if (Array.isArray(action.payload)) {
                state.data = action.payload;
            }
        },
        appendImages(state, action) {
            const incoming = Array.isArray(action.payload)
                ? action.payload
                : [];
            if (!Array.isArray(state.data)) {
                state.data = [];
            }
            const unique = incoming.filter(
                (url) => url && !state.data.includes(url),
            );
            state.data = [...state.data, ...unique];
        },
    },
    extraReducers: (builder) => {
        handleRequestThunk({ builder, request: getImages });
        builder
            .addCase(uploadFile.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(uploadFile.fulfilled, (state, action) => {
                state.isLoading = false;
                state.error = null;
                const responseData = action.payload || {};
                let newImages = [];

                if (responseData.images && Array.isArray(responseData.images)) {
                    newImages = responseData.images.map((url) =>
                        typeof url === 'string'
                            ? url.replace(/^"(.*)"$/, '$1')
                            : url,
                    );
                } else if (Array.isArray(responseData)) {
                    newImages = responseData;
                } else if (
                    responseData.data &&
                    Array.isArray(responseData.data)
                ) {
                    newImages = responseData.data;
                }

                if (!Array.isArray(state.data)) {
                    state.data = [];
                }

                if (newImages.length > 0) {
                    const existingImages = state.data;
                    const uniqueNewImages = newImages.filter(
                        (newImg) => newImg && !existingImages.includes(newImg),
                    );
                    state.data = [...existingImages, ...uniqueNewImages];
                }
            })
            .addCase(uploadFile.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload || action.error?.message;
            })
            .addCase(deleteImage.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(deleteImage.fulfilled, (state, action) => {
                state.isLoading = false;
                const deletedImageUrl = action.meta?.arg?.imageUrl;

                if (deletedImageUrl && Array.isArray(state.data)) {
                    state.data = state.data.filter(
                        (url) => url !== deletedImageUrl,
                    );
                }
            })
            .addCase(deleteImage.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload || action.error?.message;
            });
    },
});

export const {
    saveFormData,
    clearFormData,
    removeImageFromData,
    reorderImagesInState,
    appendImages,
} = imageUploadSlice.actions;

export const imageUpload = imageUploadSlice.reducer;
